import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ShoppingCart } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { formatItemCode } from '../../utils/itemCodeFormatter';
import usePermissions from '../../hooks/usePermissions';

const EstimateDetailModal = ({ estimate, isOpen, onClose, loadingDetail = false, onConvertToSale }) => {
  const { hasPermission } = usePermissions();
  const [itemImage, setItemImage] = useState(null);

  // ── Edit & Finalize mode ─────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [overrideGoldRate, setOverrideGoldRate] = useState('');
  const [overrideMaking, setOverrideMaking] = useState('');
  const [overrideCustomsPct, setOverrideCustomsPct] = useState('');
  const [overrideDiscount, setOverrideDiscount] = useState('');

  // Converting an estimate to a bill is a finalize/approve action
  const canConvert = hasPermission('view-estimates', 'approve');

  useEffect(() => {
    if (isOpen && estimate?.item_code) {
      fetch(`/api/jewelry/details/${estimate.item_code}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => { if (data?.image_url) setItemImage(data.image_url); })
        .catch(() => { });
    }
    if (!isOpen) {
      setItemImage(null);
      setEditMode(false);
      setOverrideGoldRate('');
      setOverrideMaking('');
      setOverrideCustomsPct('');
      setOverrideDiscount('');
    }
  }, [isOpen, estimate]);

  if (!isOpen || !estimate) return null;

  const isUSD = estimate.currency === 'USD';

  // Collect the active overrides (only non-empty values)
  const getOverrides = () => {
    const ov = {};
    if (overrideGoldRate !== '') ov.goldRate = parseFloat(overrideGoldRate);
    if (overrideMaking !== '') ov.making = parseFloat(overrideMaking);
    if (overrideCustomsPct !== '') ov.customsPct = parseFloat(overrideCustomsPct);
    if (overrideDiscount !== '') ov.discount = parseFloat(overrideDiscount);
    return ov;
  };

  const handleConvert = () => {
    if (onConvertToSale) {
      onConvertToSale(estimate, getOverrides());
    }
  };

  // ── formatters ──────────────────────────────────────────
  const fmtINR = (val) => {
    const n = parseFloat(val);
    if (!n && n !== 0) return '—';
    return '₹' + Math.round(n).toLocaleString('en-IN');
  };
  const fmtUSD = (val) => {
    const n = parseFloat(val);
    if (!n && n !== 0) return '—';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtWt = (val, dec = 3) => {
    const n = parseFloat(val);
    return isNaN(n) ? '—' : n.toFixed(dec) + 'g';
  };
  const fmtPct = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? '—' : n.toFixed(2) + '%';
  };
  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return '—'; }
  };

  // Parse stone_details from DB (stored as JSON array or string)
  let stoneRows = [];
  try {
    const raw = estimate.stone_details;
    stoneRows = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : []);
  } catch { stoneRows = []; }

  const totalStoneCost = stoneRows.reduce((s, r) => s + (parseFloat(r.cost) || 0), 0);

  // ── sub-components ───────────────────────────────────────
  const Card = ({ children, className = '' }) => (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );

  const CardHead = ({ title, color = 'bg-gray-800' }) => (
    <div className={`${color} text-white text-xs font-bold uppercase tracking-widest px-4 py-2`}>{title}</div>
  );

  const InfoRow = ({ label, value, valueClass = '' }) => (
    value && value !== '—' ? (
      <div className="flex justify-between items-center px-4 py-2 border-b border-gray-50 last:border-0">
        <span className="text-xs text-gray-500 flex-shrink-0 w-44">{label}</span>
        <span className={`text-sm font-medium text-gray-900 text-right ${valueClass}`}>{value}</span>
      </div>
    ) : null
  );

  const statusColors = { active: 'bg-green-100 text-green-700', converted: 'bg-blue-100 text-blue-700', expired: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-600' };
  const statusClass = statusColors[estimate.status] || 'bg-gray-100 text-gray-600';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-3">
      <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[94vh] overflow-y-auto flex flex-col">

        {/* ── HEADER ── */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-800 to-slate-700 text-white px-6 py-4 rounded-t-2xl flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold tracking-tight">{estimate.estimate_number}</h2>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusClass}`}>
                {estimate.status?.toUpperCase()}
              </span>
              <span className="text-xs bg-white bg-opacity-20 px-2 py-0.5 rounded-full font-medium">
                {estimate.currency}
              </span>
            </div>
            <p className="text-slate-300 text-sm mt-0.5">{estimate.customer_name}</p>
          </div>
          <button onClick={onClose} className="hover:bg-red-500 p-1.5 rounded-full transition mt-0.5">
            <X size={20} />
          </button>
        </div>

        {/* Loading bar — shown while fetching full record */}
        {loadingDetail && (
          <div className="h-1 bg-amber-400 animate-pulse w-full" />
        )}

        <div className="p-4 space-y-3">
          <PageIdentifier pageId={SCREEN_IDS?.ESTIMATES?.ESTIMATE_DETAILS_MODAL || 'EST-003M1'} pageName="Estimate Details" />

          {/* ── TOP: IMAGE + QUICK FACTS ── */}
          <div className="flex gap-3">
            {/* Image */}
            <div className="flex-shrink-0">
              {itemImage ? (
                <img src={itemImage} alt={estimate.item_code}
                  className="w-32 h-32 object-contain rounded-xl border-2 border-gray-200 shadow bg-white" />
              ) : (
                <div className="w-32 h-32 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <span className="text-gray-400 text-xs text-center">No Image</span>
                </div>
              )}
            </div>

            {/* Quick facts */}
            <Card className="flex-1">
              <CardHead title="Jewelry" color="bg-amber-600" />
              <InfoRow label="Item Code" value={formatItemCode(estimate.item_code)} />
              {estimate.jewelry_name && <InfoRow label="Name" value={estimate.jewelry_name} />}
              <InfoRow label="Purity" value={estimate.gold_purity ? `${estimate.gold_purity}K` : null} />
              {estimate.is_billing_draft && (
                <InfoRow label="Gold Rate" value={estimate.gold_price ? `${isUSD ? fmtUSD(estimate.gold_price) : fmtINR(estimate.gold_price)}/g` : null} />
              )}
              <InfoRow label="Gross Weight" value={fmtWt(estimate.gross_weight)} />
              <InfoRow label="Net Weight" value={fmtWt(estimate.net_weight)} />
              {estimate.fine_weight > 0 && <InfoRow label="Fine Weight" value={fmtWt(estimate.fine_weight)} />}
              {estimate.is_billing_draft && parseFloat(estimate.color_stone_carats) > 0 && (
                <InfoRow label="Stone Wt" value={`${parseFloat(estimate.color_stone_carats).toFixed(3)} Ct`} />
              )}
              {estimate.is_billing_draft && parseFloat(estimate.diamond_carats) > 0 && (
                <InfoRow label="Diamond Wt" value={`${parseFloat(estimate.diamond_carats).toFixed(3)} Ct`} />
              )}
            </Card>
          </div>

          {/* ── CUSTOMER + DATES ── */}
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardHead title="Customer" color="bg-slate-600" />
              <InfoRow label="Name" value={estimate.customer_name} />
              <InfoRow label="Phone" value={estimate.customer_phone} />
              {estimate.is_billing_draft
                ? <InfoRow label="Address" value={[estimate.customer_city, estimate.customer_state].filter(Boolean).join(', ')} />
                : <InfoRow label="Email" value={estimate.customer_email} />}
            </Card>
            {estimate.is_billing_draft ? (
              <Card>
                <CardHead title="Date" color="bg-slate-600" />
                <InfoRow label="Bill Date" value={fmtDate(estimate.date)} />
              </Card>
            ) : (
              <Card>
                <CardHead title="Dates" color="bg-slate-600" />
                <InfoRow label="Estimate Date" value={fmtDate(estimate.estimate_date)} />
                <InfoRow label="Valid Until" value={fmtDate(estimate.valid_until)} />
                <InfoRow label="Created By" value={estimate.created_by_username} />
              </Card>
            )}
          </div>

          {/* ── GOLD CALCULATION ── */}
          <Card>
            <CardHead title="Gold Calculation" color="bg-yellow-700" />
            <div className="bg-yellow-50 border-t border-yellow-100 px-4 py-2 flex justify-between items-center">
              <span className="text-xs font-bold text-yellow-800 uppercase tracking-wide">Total Gold Amount</span>
              <span className="text-base font-bold text-yellow-900">{fmtINR(estimate.total_gold_amount)}</span>
            </div>
          </Card>

          {/* ── STONE DETAILS ── */}
          {stoneRows.length > 0 && (
            <Card>
              <CardHead title="Stone Details" color="bg-purple-700" />
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-purple-50 text-purple-700">
                      <th className="text-left px-4 py-2 font-semibold">Stone</th>
                      <th className="text-left px-4 py-2 font-semibold">Code</th>
                      <th className="text-right px-4 py-2 font-semibold">Weight (g)</th>
                      <th className="text-right px-4 py-2 font-semibold">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stoneRows.map((s, i) => {
                      // Web saves: { name, code, weight, rate, cost }
                      // Mobile saves: { stoneCode, weight, rate, cost, unit }
                      const code = s.code || s.stoneCode || '—';
                      const name = s.name || s.stoneCode || s.code || '—';
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 text-gray-800 font-medium">{name}</td>
                          <td className="px-4 py-2 text-gray-500">{code}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{parseFloat(s.weight || 0).toFixed(3)}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-900">{fmtINR(s.cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-purple-50 border-t border-purple-100 px-4 py-2 flex justify-between items-center">
                <span className="text-xs font-bold text-purple-800 uppercase tracking-wide">Total Stone Cost</span>
                <span className="text-base font-bold text-purple-900">{fmtINR(totalStoneCost)}</span>
              </div>
            </Card>
          )}

          {/* ── CERTIFICATION ── */}
          {estimate.certification_required && parseFloat(estimate.certification_charges) > 0 && (
            <Card>
              <CardHead title="Certification" color="bg-teal-700" />
              <InfoRow label="Diamond Carats" value={estimate.diamond_carats ? `${parseFloat(estimate.diamond_carats).toFixed(3)} Ct` : null} />
              <InfoRow label="Certification Charges" value={fmtINR(estimate.certification_charges)} />
            </Card>
          )}

          {/* ── SUMMARY ── */}
          <Card>
            <CardHead title="Summary" color="bg-slate-800" />
            <InfoRow label="Gold Amount" value={fmtINR(estimate.total_gold_amount)} />
            {totalStoneCost > 0 && <InfoRow label="Stone Amount" value={fmtINR(totalStoneCost)} />}
            {parseFloat(estimate.certification_charges) > 0 && <InfoRow label="Certification" value={fmtINR(estimate.certification_charges)} />}
            {isUSD && estimate.usd_to_inr_rate && <InfoRow label="USD → INR Rate" value={`₹${parseFloat(estimate.usd_to_inr_rate).toFixed(2)}`} />}
            <InfoRow
              label={isUSD ? `Customs / Tax (${fmtPct(estimate.tax_rate)})` : `GST (${fmtPct(estimate.tax_rate)})`}
              value={fmtINR(estimate.tax_amount)}
            />
            {/* Grand Total box */}
            <div className="bg-slate-800 text-white px-4 py-3 mt-1">
              {(estimate.grand_total_inr || estimate.grand_total) && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold tracking-wide">Grand Total (INR)</span>
                  <span className="text-xl font-extrabold text-amber-400">
                    {fmtINR(estimate.grand_total_inr || estimate.grand_total)}
                  </span>
                </div>
              )}
              {isUSD && parseFloat(estimate.grand_total_usd) > 0 && (
                <div className="flex justify-between items-center mt-1 pt-1 border-t border-slate-600">
                  <span className="text-sm font-bold tracking-wide">Grand Total (USD)</span>
                  <span className="text-xl font-extrabold text-green-400">
                    {fmtUSD(estimate.grand_total_usd)}
                  </span>
                </div>
              )}
            </div>
            {/* On-screen only: cumulative reduction vs. this item's first-quoted
                standard price. Never shown on PDFs/email/WhatsApp. */}
            {(() => {
              const catalog = parseFloat(estimate.catalog_price_inr);
              const current = parseFloat(estimate.grand_total_inr || estimate.grand_total);
              const reduced = catalog - current;
              if (!(catalog > 0) || !(current > 0) || reduced <= 0) return null;
              return (
                <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-2 flex justify-between items-center">
                  <span className="text-xs font-semibold text-emerald-700">You've Reduced</span>
                  <span className="text-sm font-bold text-emerald-700">{fmtINR(reduced)}</span>
                </div>
              );
            })()}
          </Card>

          {/* ── NOTES ── */}
          {estimate.notes && (
            <Card>
              <CardHead title="Notes" color="bg-gray-500" />
              <p className="px-4 py-3 text-sm text-gray-700 whitespace-pre-line">{estimate.notes}</p>
            </Card>
          )}

          {/* ── EDIT & FINALIZE (manager+ only) ── */}
          {canConvert && estimate.status === 'active' && (
            <Card>
              <div className={`${editMode ? 'bg-amber-700' : 'bg-amber-600'} text-white text-xs font-bold uppercase tracking-widest px-4 py-2 flex justify-between items-center`}>
                <span>Edit &amp; Finalize Rates</span>
                <button
                  onClick={() => setEditMode(m => !m)}
                  className="text-xs bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-0.5 rounded transition"
                >
                  {editMode ? 'Cancel' : 'Edit Rates'}
                </button>
              </div>
              {editMode ? (
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Gold Rate (24K/10g) override</label>
                    <input
                      type="number"
                      value={overrideGoldRate}
                      onChange={e => setOverrideGoldRate(e.target.value)}
                      placeholder={`Original: ${estimate.gold_rate_24k ?? '—'}`}
                      className="w-full border border-amber-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Making Charge (₹/g) override</label>
                    <input
                      type="number"
                      value={overrideMaking}
                      onChange={e => setOverrideMaking(e.target.value)}
                      placeholder={`Original: ${estimate.making_charge_per_g ?? '—'}`}
                      className="w-full border border-amber-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Customs % override</label>
                    <input
                      type="number"
                      value={overrideCustomsPct}
                      onChange={e => setOverrideCustomsPct(e.target.value)}
                      placeholder={`Original: ${estimate.tax_rate ?? '—'}`}
                      className="w-full border border-amber-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Discount (₹) override</label>
                    <input
                      type="number"
                      value={overrideDiscount}
                      onChange={e => setOverrideDiscount(e.target.value)}
                      placeholder="0"
                      className="w-full border border-amber-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <p className="col-span-2 text-xs text-gray-400 italic">
                    Overrides are used for billing only — not saved to the estimate.
                  </p>
                </div>
              ) : (
                <p className="px-4 py-3 text-xs text-gray-500">
                  Click <strong>Edit Rates</strong> to override gold rate, making charge, customs % or discount before converting to a bill.
                </p>
              )}
            </Card>
          )}

        </div>

        {/* ── FOOTER ── */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-between items-center rounded-b-2xl">
          <div>
            {canConvert && estimate.status === 'active' && (
              <button
                onClick={handleConvert}
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition"
              >
                <ShoppingCart size={15} />
                Convert to Sale
              </button>
            )}
          </div>
          <button onClick={onClose} className="bg-slate-700 hover:bg-slate-800 text-white px-6 py-2 rounded-lg text-sm font-medium transition">
            Close
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default EstimateDetailModal;
